import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { t as getFixedT } from "@/i18n";
import { BRAND } from "@/lib/brand";
import type { SupportedLanguage } from "@/lib/site";
import "./Header.css";

interface NavItem {
  href: string;
  id: string;
  key: string;
}

const NAV_ITEMS: NavItem[] = [
  { href: "#why-us", id: "why-us", key: "nav.whyUs" },
  { href: "#programs", id: "programs", key: "nav.programs" },
  { href: "#parents-opinions", id: "parents-opinions", key: "nav.parentsOpinions" },
  { href: "#faq", id: "faq", key: "nav.faq" },
];

/** Scroll position (px) past which the header gains its solid background/shadow. */
const SOLID_THRESHOLD = 24;
/** Scroll position (px) past which the header shrinks to its compact size. */
const SHRINK_THRESHOLD = 80;

interface HeaderProps {
  lang: SupportedLanguage;
  otherLang: SupportedLanguage;
}

/** Sticky header — transparent over the hero, solid + shrunk once the page scrolls
 * (09-landing-page.md §2.2). The scroll listener only ever runs client-side; the
 * prerendered/first-paint state is always "solid, full-height" (safer default — never risks
 * unreadable nav text or a mid-transition flash if JS is slow to hydrate).
 *
 * A `ResizeObserver` on the header element keeps `--layout-topbar-height` (consumed as
 * `scroll-margin-top` by every anchorable section, see HomePage.css/Hero.css) in sync with the
 * header's *real* rendered height at all times — including mid-transition while it shrinks/grows —
 * so clicking a nav link never leaves a heading tucked under the sticky bar and the page never
 * jumps when the header's own height changes.
 */
export function Header({ lang, otherLang }: HeaderProps) {
  const t = getFixedT(lang);
  const location = useLocation();
  const [scrolled, setScrolled] = useState(true);
  const [shrunk, setShrunk] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > SOLID_THRESHOLD);
      setShrunk(window.scrollY > SHRINK_THRESHOLD);
      // Scroll-spy: the active link is the LAST nav section whose top has crossed just below the
      // header. At the very top (only the hero showing, no nav section crossed yet) nothing is
      // active — so "Why Us" is no longer wrongly highlighted while you're still on the hero.
      const headerH = headerRef.current?.getBoundingClientRect().height ?? 72;
      const line = headerH + 24;
      let current: string | null = null;
      for (const item of NAV_ITEMS) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= line) current = item.id;
      }
      setActiveId(current);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    function syncHeightVar() {
      if (!el) return;
      document.documentElement.style.setProperty(
        "--layout-topbar-height",
        `${Math.round(el.getBoundingClientRect().height)}px`,
      );
    }
    syncHeightVar();
    const observer = new ResizeObserver(syncHeightVar);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const otherLangHref = `/${otherLang}${location.hash || ""}`;

  return (
    <header
      ref={headerRef}
      className={`fa-header${scrolled ? " fa-header--solid" : ""}${shrunk ? " fa-header--shrunk" : ""}`}
    >
      <div className="fa-header__inner">
        <a href="#hero" className="fa-header__brand">
          <img
            src="/academy-logo.jpeg"
            alt={lang === "ar" ? BRAND.nameAr : BRAND.nameEn}
            className="fa-header__logo"
            width="44"
            height="44"
          />
          <span className="fa-header__brand-name">
            {lang === "ar" ? BRAND.nameAr : BRAND.nameEn}
          </span>
        </a>
        <nav aria-label={t("nav.label")} className="fa-header__nav">
          <ul>
            {NAV_ITEMS.map((item) => {
              const isActive = activeId === item.id;
              return (
                <li key={item.href}>
                  <a
                    href={item.href}
                    className={`fa-header__nav-link${isActive ? " fa-header__nav-link--active" : ""}`}
                    aria-current={isActive ? "true" : undefined}
                  >
                    {t(item.key)}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="fa-header__actions">
          <a
            href="#lead-form"
            className="fa-btn fa-btn--primary fa-header__cta"
          >
            {t("hero.cta")}
          </a>
          <Link
            to={otherLangHref}
            hrefLang={otherLang}
            lang={otherLang}
            className="fa-header__lang-switch"
          >
            {t("nav.languageSwitchShort")}
          </Link>
        </div>
      </div>
    </header>
  );
}
